import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDoc, collection, query, where, getDocs} from 'firebase/firestore';
import { db } from '../firebase';
import { leaveOldTeam } from '../utils/teamUtils';
import useSnackbar from './useSnackbar';
import { validateTeamName } from '../utils/validation';
import { logError } from '../utils/security';
import { teamCreationLimiter, checkRateLimit } from '../utils/rateLimiter';

/**
 * Hook to manage team creation and optional switching from an existing team.
 */
function useCreateTeam(user, currentTeam, setCurrentTeam) {
  const { t } = useTranslation();
  const [teamName, setTeamName] = useState('');
  const [loading, setLoading] = useState(false);
  const [openSwitchDialog, setOpenSwitchDialog] = useState(false);
  const { openSnackbar, snackbarMessage, snackbarSeverity, showAlert, closeSnackbar } = useSnackbar();

  const translateValidationError = (result) => (result.errorKey ? t(result.errorKey, result.errorParams) : result.error);

  const createTeamHelper = async () => {
    const teamValidation = validateTeamName(teamName);
    if (!teamValidation.valid) {
      throw new Error(translateValidationError(teamValidation));
    }
    
    return await addDoc(collection(db, 'teams'), {
      name: teamValidation.sanitized,
      adminId: user.uid,
      members: [user.uid],
      createdAt: new Date(),
    });
  };

  const handleCreate = async () => {
    try {
      // Rate limiting check
      checkRateLimit(teamCreationLimiter, user.uid, 'team creation');
      
      const teamValidation = validateTeamName(teamName);
      if (!teamValidation.valid) {
        showAlert(translateValidationError(teamValidation), 'warning');
        return;
      }
      
      if (loading) return;
      setLoading(true);

      const q = query(collection(db, 'teams'), where('name', '==', teamValidation.sanitized));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        showAlert(t('teamExists'), 'warning');
        return;
      }

      if (currentTeam) {
        setOpenSwitchDialog(true);
        return;
      }

      const docRef = await createTeamHelper();
      setCurrentTeam({ 
        id: docRef.id, 
        name: teamValidation.sanitized, 
        adminId: user.uid, 
        members: [user.uid], 
        createdAt: new Date() 
      });
      showAlert(t('teamCreated', { teamName: teamValidation.sanitized }), 'success');
      setTeamName('');
    } catch (error) {
      logError(error, 'TEAM_CREATION');
      if (error.message.toLowerCase().includes('too many')) {
        showAlert(t('tooManyRequests'), 'warning');
      } else {
        showAlert(t('errorCreatingTeam'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmSwitchTeam = async () => {
    try {
      const teamValidation = validateTeamName(teamName);
      if (!teamValidation.valid) {
        showAlert(translateValidationError(teamValidation), 'warning');
        return;
      }
      setLoading(true);
      await leaveOldTeam(user.uid, currentTeam);
      setCurrentTeam(null);
      const docRef = await createTeamHelper();
      setCurrentTeam({ 
        id: docRef.id, 
        name: teamValidation.sanitized, 
        adminId: user.uid, 
        members: [user.uid], 
        createdAt: new Date() 
      });
      showAlert(t('teamCreated', { teamName: teamValidation.sanitized }), 'success');
      setTeamName('');
    } catch (error) {
      logError(error, 'TEAM_SWITCH');
      showAlert(t('errorCreatingOrSwitchingTeam'), 'error');
    } finally {
      setLoading(false);
      setOpenSwitchDialog(false);
    }
  };

  return { teamName, setTeamName, loading, openSwitchDialog, setOpenSwitchDialog, handleCreate, confirmSwitchTeam, openSnackbar, snackbarMessage, snackbarSeverity, closeSnackbar };
}

export default useCreateTeam;
